import { useQuery } from '@tanstack/react-query';
import { Phone } from 'lucide-react';
import { EmptyState, Notice, PageHeader, StatusIndicator, Table, type IndicatorState } from '@voice/ui';
import { useMutation } from '@tanstack/react-query';
import { errorText } from '../components/common';
import { api } from '../lib/api';
import { useSoftphone } from '../lib/softphone';
import styles from './Pages.module.css';

type State = 'offline' | 'dnd' | 'busy' | 'ringing' | 'available';
const LABEL: Record<State, { text: string; tone: IndicatorState }> = {
  available: { text: 'Erreichbar', tone: 'ok' },
  ringing: { text: 'Klingelt', tone: 'ringing' },
  busy: { text: 'Im Gespräch', tone: 'busy' },
  dnd: { text: 'Nicht stören', tone: 'off' },
  offline: { text: 'Nicht angemeldet', tone: 'off' },
};

export function Team() {
  const query = useQuery({ queryKey: ['presence'], queryFn: () => api<{ extensions: { number: string; name: string; state: State }[] }>('/presence'), refetchInterval: 4000 });
  const phone = useSoftphone();
  // Ohne Browser-Telefon klingelt zuerst das eigene Tischtelefon, danach wird gewählt.
  const dial = useMutation({ mutationFn: (number: string) => api('/calls/dial', { method: 'POST', body: { number } }) });
  const call = (n: string) => (phone.status === 'ready' ? void phone.dial(n) : dial.mutate(n));
  return (
    <div className={styles.page}>
      <PageHeader title="Team" />
      {query.isError && <Notice tone="error">Die Übersicht konnte nicht geladen werden.</Notice>}
      {dial.isError && <Notice tone="error">{errorText(dial.error)}</Notice>}
      {dial.isSuccess && <Notice tone="success">Dein Telefon klingelt – nach dem Abheben wird gewählt.</Notice>}
      {query.data?.extensions.length === 0 && <EmptyState>Noch keine Nebenstellen.</EmptyState>}
      {query.data && query.data.extensions.length > 0 && (
        <Table aria-label="Team">
          <thead>
            <tr>
              <th>Nebenstelle</th>
              <th>Name</th>
              <th>Zustand</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {query.data.extensions.map((e) => (
              <tr key={e.number}>
                <th scope="row">{e.number}</th>
                <td>{e.name}</td>
                <td>
                  <StatusIndicator state={LABEL[e.state].tone} label={LABEL[e.state].text} />
                </td>
                <td>
                  <button type="button" className={styles.iconBtn} onClick={() => call(e.number)} aria-label={`${e.name} anrufen`}>
                    <Phone size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
